import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { HistoryActionService } from '@/api/generated/services/HistoryActionService'

interface ActionTypeSelectProps {
  selectedActionType: string
  selectedActionTypeName: string
  onSelect: (actionType: string, actionTypeName: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
  showAllOption?: boolean
}

interface ActionTypeOption {
  key: string
  name: string
}

const ActionTypeSelect: React.FC<ActionTypeSelectProps> = ({
  selectedActionType,
  selectedActionTypeName,
  onSelect,
  disabled = false,
  error = null,
  placeholder = '액션 종류를 선택하세요',
  showAllOption = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [actionTypes, setActionTypes] = useState<ActionTypeOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  // Fetch action types
  const fetchActionTypes = useCallback(async () => {
    setIsLoading(true)
    setFetchError(null)

    try {
      const response = await HistoryActionService.appsHistoryViewsListActionTypes()

      if (response) {
        // Convert Record<string, string> to ActionTypeOption[]
        const options: ActionTypeOption[] = Object.entries(response).map(([key, name]) => ({
          key,
          name: String(name)
        }))
        setActionTypes(options)
      }
    } catch (err) {
      console.error('Failed to fetch action types:', err)
      setFetchError('액션 종류 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchActionTypes()
  }, [fetchActionTypes])

  // Filter action types based on search term
  const filteredActionTypes = useMemo(() => {
    if (!searchTerm.trim()) return actionTypes
    const searchLower = searchTerm.toLowerCase().trim()
    return actionTypes.filter(at =>
      at.name.toLowerCase().includes(searchLower) ||
      at.key.toLowerCase().includes(searchLower)
    )
  }, [actionTypes, searchTerm])

  const handleSelect = (actionType: ActionTypeOption) => {
    onSelect(actionType.key, actionType.name)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect('', '')
    setSearchTerm('')
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (!isOpen) {
        setSearchTerm('')
      }
    }
  }

  return (
    <div className="relative">
      {/* Selection Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: error ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1],
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          color: selectedActionTypeName ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <span className="truncate">{selectedActionTypeName || placeholder}</span>
          <div className="flex items-center gap-2">
            {selectedActionType && selectedActionType.trim() !== '' && (
              <span
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                style={{ fontSize: '14px' }}
              >
                ✕
              </span>
            )}
            <ChevronDown
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border-2 rounded shadow-lg"
          style={{
            borderColor: designTokens.colors.gray[1]
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
            <div className="relative">
              <Search
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: designTokens.colors.gray[3] }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="액션 종류 검색..."
                className="w-full pl-2 pr-3 py-2 border rounded"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6]
                }}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Dropdown Content */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                액션 종류 목록을 불러오는 중...
              </div>
            ) : fetchError ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.error.DEFAULT
                }}
              >
                {fetchError}
              </div>
            ) : actionTypes.length === 0 ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                액션 종류가 없습니다.
              </div>
            ) : filteredActionTypes.length === 0 ? (
              <div
                className="p-3 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[4]
                }}
              >
                검색 결과가 없습니다.
              </div>
            ) : (
              <>
                {/* All Option */}
                {showAllOption && !searchTerm.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      onSelect('', '')
                      setIsOpen(false)
                      setSearchTerm('')
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    전체
                  </button>
                )}

                {filteredActionTypes.map((actionType) => (
                  <button
                    key={actionType.key}
                    type="button"
                    onClick={() => handleSelect(actionType)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    {actionType.name}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="mt-1 text-sm"
          style={{
            color: designTokens.colors.error.DEFAULT,
            fontFamily: designTokens.typography.fontFamily.sans[0]
          }}
        >
          {error}
        </div>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default ActionTypeSelect
